import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Target, ShieldCheck } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import PaymentSolutionFinder from "@/components/PaymentSolutionFinder";
import TrustBar from "@/components/TrustBar";
import RelatedTools from "@/components/RelatedTools";
import SolutionsCrossSell from "@/components/SolutionsCrossSell";
import { fadeUp, stagger, viewportOnce } from "@/utils/motionVariants";

export default function PaymentSolutionFinderPage() {
  return (
    <div>
      <SEOHead title="Payment Solution Finder" description="Answer a few quick questions and we'll recommend the best payment solution for your business. Free, instant, no obligation." path="/payment-finder" />

      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">Payment Solution Finder</motion.p>
            <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6">Not sure where to <span className="gradient-text">start?</span></motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed mb-8">Answer 7 quick questions and we&apos;ll recommend the best payment solution for your business. Takes less than a minute.</motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
              <a href="#quiz" className="bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors btn-shine">Find my best option (60 sec) <ArrowRight size={16} /></a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <TrustBar />

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Zap, title: "60 seconds", desc: "Seven quick questions. No jargon, no sign-up required." },
              { icon: Target, title: "Personalised result", desc: "Get a recommendation tailored to your business type, size, and needs." },
              { icon: ShieldCheck, title: "No obligation", desc: "See your result instantly. Speak to us only if you want to." },
            ].map((item) => (
              <motion.div key={item.title} variants={fadeUp} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-brand-aqua-light flex items-center justify-center flex-shrink-0">
                  <item.icon size={20} className="text-brand-aqua" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-brand-black mb-1">{item.title}</h3>
                  <p className="text-sm text-brand-grey leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section id="quiz" className="py-20 bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <PaymentSolutionFinder />
        </div>
      </section>

      <RelatedTools currentPath="/payment-finder" />
      <SolutionsCrossSell currentPath="/payment-finder" />

      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl font-bold text-brand-black mb-3">Prefer to speak to someone?</h2>
          <p className="text-sm text-brand-grey mb-6">No problem. Call us on <a href="tel:+448003682989" className="text-brand-aqua font-semibold">0800 368 2989</a> or book a call at a time that suits you.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="/book" className="bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors btn-shine">Book a Call <ArrowRight size={16} /></a>
            <Link to="/contact" className="bg-white border border-brand-aqua text-brand-aqua-dark font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 hover:bg-brand-aqua-light transition-colors">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
